import { useState, useEffect, useCallback, useRef } from 'react';
import { getHistory, addHistoryEntry as addHistLocal, removeHistoryEntry as removeHistLocal, clearHistory as clearHistLocal } from '@/lib/storage';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import type { HistoryEntry } from '@/types';

interface UseHistoryOptions {
  userId?: string | null;
  isAuthenticated?: boolean;
}

/**
 * Hook pour gérer l'historique des chants consultés avec cloud sync
 */
export function useHistory(options: UseHistoryOptions = {}) {
  const { userId, isAuthenticated = false } = options;

  // All hooks must be called unconditionally and in the same order
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);
  const userIdRef = useRef(userId);

  // Update ref when userId changes
  useEffect(() => {
    if (userIdRef.current !== userId) {
      userIdRef.current = userId;
      loadedRef.current = false;
    }
  }, [userId]);

  const loadHistory = useCallback(async () => {
    try {
      const localHist = await getHistory();
      setHistory(localHist);
      setLoading(false);

      // Load from cloud only once per session
      if (isAuthenticated && userId && supabase && isSupabaseConfigured() && !loadedRef.current) {
        loadedRef.current = true;
        try {
          const { data, error } = await supabase
            .from('user_history')
            .select('song_id, viewed_at, duration')
            .eq('user_id', userId)
            .order('viewed_at', { ascending: false })
            .limit(50);

          if (!error && data && data.length > 0) {
            const cloudHist: HistoryEntry[] = data.map(item => ({
              songId: item.song_id,
              timestamp: new Date(item.viewed_at).getTime(),
              duration: item.duration || 0,
              deviceWidth: 0
            }));
            setHistory(cloudHist);
            localStorage.setItem('jem-history', JSON.stringify(cloudHist));
            console.log('History loaded from cloud:', cloudHist.length);
          }
        } catch (cloudError) {
          console.warn('Cloud history load error');
        }
      }
    } catch (error) {
      console.error('Error loading history:', error);
      setLoading(false);
    }
  }, [isAuthenticated, userId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const removeEntry = useCallback(async (songId: number) => {
    try {
      await removeHistLocal(songId);
      setHistory(prev => prev.filter(h => h.songId !== songId));

      if (isAuthenticated && userId && supabase && isSupabaseConfigured()) {
        try {
          await supabase
            .from('user_history')
            .delete()
            .eq('user_id', userId)
            .eq('song_id', songId);
        } catch (cloudError) {
          console.warn('Cloud delete error:', cloudError);
        }
      }

      return true;
    } catch (error) {
      console.error('Error removing history entry:', error);
      return false;
    }
  }, [isAuthenticated, userId]);

  const clearHistory = useCallback(async () => {
    try {
      await clearHistLocal();
      setHistory([]);

      if (isAuthenticated && userId && supabase && isSupabaseConfigured()) {
        try {
          await supabase
            .from('user_history')
            .delete()
            .eq('user_id', userId);
        } catch (cloudError) {
          console.warn('Cloud clear error:', cloudError);
        }
      }

      return true;
    } catch (error) {
      console.error('Error clearing history:', error);
      return false;
    }
  }, [isAuthenticated, userId]);

  return {
    history,
    loading,
    removeEntry,
    clearHistory,
    reload: loadHistory,
  };
}

interface UseHistoryTrackerOptions {
  userId?: string | null;
  isAuthenticated?: boolean;
}

/**
 * Hook pour enregistrer automatiquement la consultation d'un chant
 * Enregistre après 15 secondes d'affichage avec cloud sync
 */
export function useHistoryTracker(
  songId: number | null,
  onHistoryAdded?: () => void,
  options: UseHistoryTrackerOptions = {}
) {
  const { userId, isAuthenticated = false } = options;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    if (!songId) return;

    startTimeRef.current = Date.now();

    timerRef.current = setTimeout(async () => {
      const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const deviceWidth = window.innerWidth;

      try {
        await addHistLocal(songId, duration, deviceWidth);

        if (isAuthenticated && userId && supabase && isSupabaseConfigured()) {
          try {
            // Standard insert to track every listen (no more unique constraint)
            const { error } = await supabase
              .from('user_history')
              .insert({
                user_id: userId,
                song_id: songId,
                duration: duration,
                viewed_at: new Date().toISOString()
              });

            if (!error) {
              console.log('History synced to cloud:', songId);
            }
          } catch (cloudError) {
            console.warn('Cloud history add error:', cloudError);
          }
        }

        if (onHistoryAdded) {
          onHistoryAdded();
        }
      } catch (error) {
        console.error('Error adding history entry:', error);
      }
    }, 15000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [songId, onHistoryAdded, userId, isAuthenticated]);
}
